import type { ViewjobOneGraphModel } from "../viewjobOneGraphModel";

import { getDeviceProperties } from "../../../shared/logging/LoggableDeviceProperties";
import { useRunOnceAfterMount } from "../../../shared/util/useRunOnceAfterMount";

import { log as logRNViewJobPerformance } from "@indeed/signals-schema/com/indeed/signals/schema/reactnative/RNViewJobPerformanceV2";
import {
  AvroOptional,
  log as logMobPageLoadInfo,
  ScreenOrientationV4,
} from "@indeed/signals-schema/com/indeed/signals/schema/viewjob/RNMobPageLoadInfoV4";

type ViewJobLoadLoggingModel = Pick<ViewjobOneGraphModel, "ctk" | "mobvjtk"> & {
  readonly features: Pick<ViewjobOneGraphModel["features"], "country">;
};

interface UseViewJobLoadLoggingParams {
  readonly viewJobModel?: ViewJobLoadLoggingModel;
  readonly navigationStartTimeMs: number;
  readonly fetchStartTimeMs: number | null;
  readonly fetchEndTimeMs: number | null;
  readonly proctorGroups?: string | null;
  readonly pageLoadSignalAlreadyEmitted?: boolean;
  readonly viewport: {
    readonly height: number;
    readonly width: number;
    readonly scale: number;
  };
}

export function useViewJobLoadLogging({
  viewJobModel,
  navigationStartTimeMs,
  fetchStartTimeMs,
  fetchEndTimeMs,
  proctorGroups,
  pageLoadSignalAlreadyEmitted = false,
  viewport: { height, width, scale },
}: UseViewJobLoadLoggingParams): void {
  useRunOnceAfterMount(() => {
    if (!viewJobModel) {
      return;
    }

    const country = viewJobModel.features.country;
    const ctk = viewJobModel.ctk;
    const mobtk = viewJobModel.mobvjtk;
    const deviceProperties = getDeviceProperties();
    const now = Date.now();

    logRNViewJobPerformance({
      ctk: AvroOptional.string(ctk ?? null),
      deviceProperties,
      baseMetrics: {
        timeToInteractiveMs: AvroOptional.long(now - navigationStartTimeMs),
      },
      fetchStartMs: fetchStartTimeMs
        ? AvroOptional.long(fetchStartTimeMs - navigationStartTimeMs)
        : AvroOptional.long(null),
      fetchEndMs: fetchEndTimeMs
        ? AvroOptional.long(fetchEndTimeMs - navigationStartTimeMs)
        : AvroOptional.long(null),
      grps: AvroOptional.string(proctorGroups),
    });

    if (!pageLoadSignalAlreadyEmitted) {
      logMobPageLoadInfo({
        deviceProperties,
        rnAppBundleVersion: AvroOptional.string(deviceProperties.rnAppBundleVersion),
        mobvjtk: AvroOptional.string(mobtk),
        jsvPage: AvroOptional.string("mvjjsv"),
        country: AvroOptional.string(country),
        ctk: AvroOptional.string(ctk),
        mobtk: AvroOptional.string(mobtk),
        scrWidth: AvroOptional.long(width),
        scrHeight: AvroOptional.long(height),
        scrRes: AvroOptional.string(`${Math.floor(width)}x${Math.floor(height)}`),
        pixelRatio: AvroOptional.float(scale),
        scrOrientation: {
          ScreenOrientationV4:
            height > width ? ScreenOrientationV4.PORTRAIT : ScreenOrientationV4.LANDSCAPE,
        },
      });
    }
  });
}
